<template>
  <div class="overview">
    <div class="innerSticker" :style="{ 'background-color': backgroundColor }">
      <section
        class="organisation-design"
        aria-labelledby="organisation-design-title"
      >
        <div class="organisation-design__heading">
          <div>
            <p class="organisation-design__eyebrow">Identity</p>
            <h2 id="organisation-design-title">Design</h2>
          </div>
          <div
            v-if="isOrgAppearanceFeatureEnabled"
            class="organisation-design__actions"
          >
            <UnitButton
              variant="text"
              size="small"
              @click="openAppearance('personal')"
            >
              My appearance
            </UnitButton>
            <UnitButton
              v-if="canManageAppearance"
              variant="text"
              size="small"
              @click="openAppearance('organisation')"
            >
              Manage organisation default
            </UnitButton>
          </div>
        </div>
        <div class="row card-header alignCentreRow">
          <div class="row alignCentreRow logo-edit-row">
            <!-- Display the Logo or a Placeholder -->
            <div class="logo-display">
              <img
                v-if="newLogoUrl"
                :src="newLogoUrl"
                alt="Organization Logo"
                class="logo-image"
              />
              <span v-else>No logo available</span>
            </div>

            <!-- Edit Button -->
            <UnitButton
              v-if="canUsePrivateLogoCommands && toggleSwitchState"
              @click="isLogoOverlayVisible = true"
              variant="text"
              size="micro"
            >
              Edit
            </UnitButton>
          </div>
          <ToggleSwitch
            v-if="canUsePrivateOrganisationCommands"
            label="Edit"
            v-model="toggleSwitchState"
            :fullWidth="false"
            :hideStatusText="true"
          />
        </div>
        <p
          v-if="isOrgAppearanceFeatureEnabled && !canManageAppearance"
          class="organisation-design__supporting"
        >
          Your colour changes only your own experience. An organisation
          administrator manages the shared default.
        </p>
      </section>
      <div class="separator_sticker noMarginTop"></div>

      <DetailList
        :rows="tableRows"
        :hideFirstRowTopBorder="true"
        :showEditButton="showEditButton"
        :showReadMoreButton="shouldShowReadMoreButton"
        @edit-row="editField($event.field)"
        @toggle-description="toggleDescription"
      />

      <!-- Organization ID with copy button -->
      <div
        class="row"
        style="
          min-height: calc(var(--control-height-default) + 2px);
          justify-content: space-between;
          border-bottom: 1px solid var(--color-border-subtle);
          padding: var(--space-5) 0;
          align-items: flex-start;
        "
      >
        <div
          class="first-column"
          style="min-width: 60%; display: flex; align-items: center"
        >
          Organisation ID
        </div>
        <div
          class="second-column"
          style="
            display: flex;
            justify-content: space-between;
            width: 50%;
            align-items: center;
          "
        >
          <span
            class="grayText"
            style="font-family: monospace; word-break: break-all"
            >{{ orgUID }}</span
          >
          <UnitButton
            variant="text"
            size="micro"
            icon-only
            aria-label="Copy organisation ID"
            style="flex-shrink: 0; margin-left: var(--space-4)"
            @click="copyOrgId"
          >
            <svg class="tinyImg" aria-hidden="true" focusable="false">
              <use href="@/assets/icons/iconset.svg#copy_tiny"></use>
            </svg>
          </UnitButton>
        </div>
      </div>

      <!-- Overlay for Description -->
      <Overlay
        v-if="canUsePrivateOrganisationCommands"
        :visible="isDescriptionOverlayVisible"
        :title="`Edit description`"
        @close="isDescriptionOverlayVisible = false"
      >
        <template #before-buttons>
          <div class="form-group">
            <textarea
              class="fullWidth"
              v-model="newDescription"
              placeholder="Enter new description"
            ></textarea>
            <AlertCard
              v-if="isTextTooLong"
              type="warning"
              id="char-limit-warning"
              iconId="error_warning"
              :canBeDismissed="false"
            >
              <template #text>
                Description cannot exceed {{ descriptionLimit }} characters.
                <p class="smallText nomargintopbottom">
                  Characters: {{ characterCount }} / {{ descriptionLimit }}
                </p>
              </template>
            </AlertCard>
          </div>
          <br />
        </template>

        <UnitButton
          variant="secondary"
          @click="isDescriptionOverlayVisible = false"
          v-haptic
        >
          Cancel
        </UnitButton>
        <UnitButton
          @click="updateDescription"
          :disabled="isTextTooLong"
          :loading="isSaving"
          loading-label="Processing..."
          v-haptic
        >
          Save
        </UnitButton>
      </Overlay>

      <!-- Overlay for Category -->
      <Overlay
        v-if="canUsePrivateOrganisationCommands"
        :visible="isCategoryOverlayVisible"
        @close="isCategoryOverlayVisible = false"
        title="Editing category"
      >
        <div class="column alignCentre">
          <select v-model="newCategory">
            <option disabled value="">
              {{ $t("CreateCustomOrg.selectCategory") }}
            </option>
            <option value="Public Governance">
              {{ $t("CreateCustomOrg.publicGovernance") }}
            </option>
            <option value="Business Association">
              {{ $t("CreateCustomOrg.businesAssociation") }}
            </option>
            <option value="Technology">
              {{ $t("CreateCustomOrg.technology") }}
            </option>
            <option value="Education/Research">
              {{ $t("CreateCustomOrg.educationResearch") }}
            </option>
            <option value="Healthcare/Sports">
              {{ $t("CreateCustomOrg.healthcareSports") }}
            </option>
            <option value="Retail and E-commerce">
              {{ $t("CreateCustomOrg.retailEcommerce") }}
            </option>
            <option value="Finance">
              {{ $t("CreateCustomOrg.finance") }}
            </option>
            <option value="Hospitality">
              {{ $t("CreateCustomOrg.hospitality") }}
            </option>
            <option value="Arts and Culture">
              {{ $t("CreateCustomOrg.artsAndCulture") }}
            </option>
            <option value="Environmental">
              {{ $t("CreateCustomOrg.environmental") }}
            </option>
            <option value="Industry">
              {{ $t("CreateCustomOrg.industry") }}
            </option>
            <option value="Other/Specialised">
              {{ $t("CreateCustomOrg.otherSpecialised") }}
            </option>
          </select>
          <UnitButton
            @click="updateCategory"
            class="overlay-save-btn"
            :loading="isSaving"
            loading-label="Processing..."
          >
            Save
          </UnitButton>
        </div>
      </Overlay>
    </div>

    <!-- Overlay for Logo -->
    <Overlay
      v-if="canUsePrivateLogoCommands"
      :visible="isLogoOverlayVisible"
      title="Edit logo"
      @close="isLogoOverlayVisible = false"
    >
      <div class="column alignCentre">
        <AlertCard
          type="info"
          id="pro-tip-logo"
          iconId="info"
          :canBeDismissed="false"
        >
          <template #text>You can use transparent background</template>
        </AlertCard>
        <ImageUploadSingleImg
          @updateImages="handleLogoUpdates"
          @logoRemoved="removeLogo"
          :existingImages="existingLogoUrls"
        />
        <UnitButton
          @click="saveLogo"
          class="overlay-save-btn"
          :loading="isSaving"
          loading-label="Processing..."
        >
          {{ isLogoRemoved ? "Close" : "Save" }}
        </UnitButton>
      </div>
    </Overlay>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import DetailList from "@/components/GenericComponents/DetailList.vue";
import Overlay from "@/components/GenericComponents/Overlay.vue";
import ToggleSwitch from "@/components/GenericComponents/ToggleSwitch.vue";
import AlertCard from "@/components/GenericComponents/AlertCard.vue";
import ImageUploadSingleImg from "@/components/GenericComponents/ImageUploadSingleImg.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import {
  isOrgAppearanceFeatureEnabled,
  isOrganisationAuthorityCommandsFeatureEnabled,
  isOrgPermissionReadsFeatureEnabled,
  isStorageAuthorityCommandsFeatureEnabled,
  isStorageAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import {
  createOrganisationCommandOperationId,
  organisationCommandAuthorityService,
} from "@/services/organisationCommandAuthorityService";
import {
  createStorageAuthorityOperationId,
  describeStorageAuthorityFile,
  storageAuthorityService,
} from "@/services/storageAuthorityService";

export default {
  name: "OrgOverview",
  components: {
    DetailList,
    Overlay,
    ToggleSwitch,
    AlertCard,
    ImageUploadSingleImg,
    UnitButton,
  },
  props: {
    unitData: {
      type: Object,
      required: true,
    },
    orgUID: {
      type: String,
      required: true,
    },
    backgroundColor: {
      type: String,
      default: "",
    },
    userRolePrivileges: {
      type: Object,
      required: false,
    },
  },
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const isPrivateOrganisationContextMode = isOrgPermissionReadsFeatureEnabled;
    const toggleSwitchState = ref(false);
    const isDescriptionExpanded = ref(false);
    const resolvedUserRolePrivileges = computed(() => {
      if (isPrivateOrganisationContextMode) return {};
      return store.getters.userRolePrivileges || {};
    });
    const semanticPermissions = computed(() => {
      if (!isPrivateOrganisationContextMode) return {};
      return store.getters.selectedOrganisationPermissions || {};
    });
    const canManageAppearance = computed(() =>
      isPrivateOrganisationContextMode
        ? semanticPermissions.value.canManageAppearance === true
        : resolvedUserRolePrivileges.value.appearanceAdministration === true
    );
    const canManageOrganisation = computed(() =>
      isPrivateOrganisationContextMode
        ? semanticPermissions.value.canManageOrganisation === true
        : resolvedUserRolePrivileges.value.monetaryParameters === true
    );
    const canUsePrivateOrganisationCommands = computed(
      () =>
        isPrivateOrganisationContextMode &&
        isOrganisationAuthorityCommandsFeatureEnabled &&
        canManageOrganisation.value
    );
    const canUsePrivateLogoCommands = computed(
      () =>
        canUsePrivateOrganisationCommands.value &&
        isStorageAuthorityCommandsFeatureEnabled
    );
    const isLogoOverlayVisible = ref(false);
    const isLogoRemoved = ref(false);
    const newLogo = ref(null);
    const newLogoUrl = ref("");
    const privateRevision = ref(props.unitData.revision || 0);
    const privateSettings = ref(null);
    const pendingPrivateOrganisationUpdate = ref(null);
    const pendingPrivateLogoUpload = ref(null);

    const readPrivateSettings = () =>
      Object.freeze({
        name: props.unitData.name,
        description: props.unitData.description || null,
        category: props.unitData.category || null,
        avatarAssetId: props.unitData.avatarAssetId || null,
        visibility: props.unitData.visibility || "private",
        economy: Object.freeze({ open: props.unitData.allowTrades === true }),
        joinPolicy: props.unitData.joinPolicy || "invite-only",
      });

    const resetPrivateSettings = () => {
      privateRevision.value = props.unitData.revision || 0;
      privateSettings.value = readPrivateSettings();
      pendingPrivateOrganisationUpdate.value = null;
      pendingPrivateLogoUpload.value = null;
      newDescription.value = props.unitData.description || "";
      newCategory.value = props.unitData.category || "";
    };

    const existingLogoUrls = computed(() =>
      newLogoUrl.value ? [newLogoUrl.value] : []
    );

    const openAppearance = (scope) =>
      router.push({
        name: "organisation-appearance",
        query: { org: props.orgUID, scope, from: "dashboard" },
      });

    const loadPrivateLogoPresentation = async () => {
      const assetId = privateSettings.value?.avatarAssetId;
      const orgId = props.orgUID;
      if (
        !isPrivateOrganisationContextMode ||
        !isStorageAuthorityReadsFeatureEnabled ||
        !assetId
      ) {
        return;
      }
      try {
        const result = await storageAuthorityService.createAssetDownloadUrl(
          assetId
        );
        if (
          props.orgUID === orgId &&
          privateSettings.value?.avatarAssetId === assetId
        ) {
          newLogoUrl.value = result.downloadUrl;
        }
      } catch (_error) {
        if (props.orgUID === orgId) newLogoUrl.value = "";
      }
    };

    onMounted(() => {
      privateSettings.value = readPrivateSettings();
      if (props.unitData.logo) {
        newLogoUrl.value = props.unitData.logo;
      }
      void loadPrivateLogoPresentation();
    });

    watch(
      () => [props.orgUID, props.unitData.revision],
      () => {
        resetPrivateSettings();
        newLogoUrl.value = props.unitData.logo || "";
        void loadPrivateLogoPresentation();
      }
    );

    const copyOrgId = () => {
      const orgId = props.orgUID;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(orgId).then(
          () => toast.success("Organization ID copied to clipboard"),
          () => toast.error("Failed to copy organization ID")
        );
      } else {
        try {
          const ta = document.createElement("textarea");
          ta.value = orgId;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          toast.success("Organization ID copied to clipboard");
        } catch (_) {
          toast.error("Failed to copy organization ID");
        }
      }
    };

    const tableRows = computed(() => [
      // {
      //   col1: "Logo",
      //   col2: newLogoUrl.value
      //     ? {
      //         html: `<img src="${newLogoUrl.value}" class="logo-image"/>`,
      //       }
      //     : "No logo available",
      //   field: "logo",
      // },
      { col1: "Name", col2: props.unitData.name, field: "name" },
      {
        col1: "Description",
        col2: {
          text: descriptionDisplay.value,
          button: toggleDescriptionButton.value,
        },
        field: "description",
      },
      { col1: "Category", col2: props.unitData.category, field: "category" },
      {
        col1: "Open economy",
        col2: toggleSwitchState.value
          ? {
              component: ToggleSwitch,
              props: {
                modelValue: props.unitData.allowTrades,
                "onUpdate:modelValue": updateAllowTrades,
                label: "",
                fullWidth: false,
                class: "no-border-bottom",
              },
            }
          : props.unitData.allowTrades
          ? "Yes"
          : "No",
        field: "allowTrades",
      },
    ]);

    const refreshPrivateOrganisation = async (orgId) => {
      const userId = store.getters.userCredentials?.data?.uid;
      if (!userId || props.orgUID !== orgId) return;
      await store.dispatch("fetchAndCommitSelectedCustomOrg", {
        userId,
        orgUID: orgId,
      });
    };

    const updatePrivateOrganisationSettings = async (overrides) => {
      if (!canUsePrivateOrganisationCommands.value) {
        const error = new Error(
          "Organisation updates require the private authority service."
        );
        error.code = "private-authority-required";
        throw error;
      }
      const orgId = props.orgUID;
      const current = privateSettings.value || readPrivateSettings();
      const settings = Object.freeze({
        ...current,
        ...overrides,
        economy: Object.freeze({
          open: overrides.economy?.open ?? current.economy.open,
        }),
      });
      const requestKey = JSON.stringify([
        orgId,
        privateRevision.value,
        settings,
      ]);
      let pending = pendingPrivateOrganisationUpdate.value;
      if (pending?.requestKey !== requestKey) {
        pending = Object.freeze({
          operationId: createOrganisationCommandOperationId("org_update"),
          requestKey,
        });
        pendingPrivateOrganisationUpdate.value = pending;
      }
      const result =
        await organisationCommandAuthorityService.updateOrganisation({
          operationId: pending.operationId,
          expectedRevision: privateRevision.value,
          orgId,
          settings,
        });
      if (props.orgUID !== orgId) {
        const error = new Error("The selected organisation changed.");
        error.code = "stale-organisation-context";
        throw error;
      }
      privateRevision.value = result.revision;
      privateSettings.value = settings;
      pendingPrivateOrganisationUpdate.value = null;
      await refreshPrivateOrganisation(orgId);
      return result;
    };

    const uploadPrivateLogoDraft = async (file) => {
      if (!canUsePrivateLogoCommands.value) {
        const error = new Error(
          "Organisation media requires the private authority service."
        );
        error.code = "private-authority-required";
        throw error;
      }
      const orgId = props.orgUID;
      const descriptor = await describeStorageAuthorityFile(
        file,
        "organisation-logo"
      );
      if (props.orgUID !== orgId || newLogo.value !== file) {
        const error = new Error("The selected organisation or logo changed.");
        error.code = "stale-organisation-context";
        throw error;
      }
      const audience =
        privateSettings.value?.visibility === "private" ? "members" : "public";
      const requestKey = JSON.stringify([orgId, audience, descriptor]);
      let pending = pendingPrivateLogoUpload.value;
      if (pending?.requestKey !== requestKey || pending.file !== file) {
        const createOperationId = createStorageAuthorityOperationId();
        const finalizeOperationId = createStorageAuthorityOperationId();
        if (createOperationId === finalizeOperationId) {
          const error = new Error("Secure logo operations are unavailable.");
          error.code = "failed-precondition";
          throw error;
        }
        pending = Object.freeze({
          createOperationId,
          finalizeOperationId,
          file,
          finalizedAssetId: null,
          requestKey,
        });
        pendingPrivateLogoUpload.value = pending;
      }
      if (pending.finalizedAssetId) return pending.finalizedAssetId;

      const grant = await storageAuthorityService.createUploadIntent({
        operationId: pending.createOperationId,
        expectedRevision: 0,
        resource: { kind: "organisation-logo", resourceId: orgId },
        audience,
        file: descriptor,
      });
      if (props.orgUID !== orgId || newLogo.value !== file) {
        const error = new Error("The selected organisation or logo changed.");
        error.code = "stale-organisation-context";
        throw error;
      }
      await storageAuthorityService.putUploadFile(grant, file);
      if (props.orgUID !== orgId || newLogo.value !== file) {
        const error = new Error("The selected organisation or logo changed.");
        error.code = "stale-organisation-context";
        throw error;
      }
      const finalized = await storageAuthorityService.finalizeUpload({
        operationId: pending.finalizeOperationId,
        expectedRevision: 0,
        uploadId: grant.uploadId,
      });
      pendingPrivateLogoUpload.value = Object.freeze({
        ...pending,
        finalizedAssetId: finalized.assetId,
      });
      return finalized.assetId;
    };

    const handleLogoUpdates = (updatedImages) => {
      if (!canUsePrivateLogoCommands.value) return;
      if (updatedImages.length > 0) {
        newLogo.value = updatedImages[0].file;
        newLogoUrl.value = updatedImages[0].url;
        isLogoRemoved.value = false;
      }
    };

    // Organisation writes go only through the private command authority; the
    // legacy direct-Firestore/Storage branches were retired.
    const removeLogo = async () => {
      if (!canUsePrivateLogoCommands.value) return null;
      newLogo.value = null;
      newLogoUrl.value = "";
      isLogoRemoved.value = true;
      pendingPrivateLogoUpload.value = null;
      return null;
    };

    const saveLogo = async () => {
      if (!canUsePrivateLogoCommands.value) return null;
      isSaving.value = true;
      try {
        let assetId = privateSettings.value?.avatarAssetId || null;
        if (isLogoRemoved.value) assetId = null;
        else if (newLogo.value)
          assetId = await uploadPrivateLogoDraft(newLogo.value);
        if (assetId !== privateSettings.value?.avatarAssetId) {
          await updatePrivateOrganisationSettings({ avatarAssetId: assetId });
        }
        newLogo.value = null;
        isLogoRemoved.value = false;
        pendingPrivateLogoUpload.value = null;
        isLogoOverlayVisible.value = false;
        if (assetId && isStorageAuthorityReadsFeatureEnabled) {
          await loadPrivateLogoPresentation();
        } else if (!assetId) {
          newLogoUrl.value = "";
        }
        toast.success("Logo updated successfully!");
      } catch (error) {
        console.error("Failed to update logo:", error);
        toast.error(error?.message || "Failed to update logo.");
      } finally {
        isSaving.value = false;
      }
      return null;
    };

    const descriptionDisplay = computed(() => {
      const description = props.unitData.description || "";
      if (isDescriptionExpanded.value) {
        return description;
      }
      if (description.length > 60) {
        return description.substring(0, 60) + "...";
      }
      return description;
    });

    const toggleDescriptionButton = computed(() => {
      return isDescriptionExpanded.value ? "Read less" : "Read more";
    });
    const shouldShowReadMoreButton = computed(() => {
      return (props.unitData.description || "").length > 60;
    });

    const toggleDescription = () => {
      isDescriptionExpanded.value = !isDescriptionExpanded.value;
    };

    const showEditButton = (row) => {
      if (!toggleSwitchState.value) return false;
      return (
        canUsePrivateOrganisationCommands.value &&
        ["Description", "Category"].includes(row.col1)
      );
    };

    const isDescriptionOverlayVisible = ref(false);
    const newDescription = ref(props.unitData.description || "");

    const isCategoryOverlayVisible = ref(false);
    const newCategory = ref(props.unitData.category || "");

    const descriptionLimit = computed(() =>
      isPrivateOrganisationContextMode ? 500 : 1500
    );
    const isTextTooLong = computed(() => {
      return newDescription.value.length > descriptionLimit.value;
    });

    const characterCount = computed(() => newDescription.value.length);

    const isSaving = ref(false);

    const editField = (field) => {
      if (!canUsePrivateOrganisationCommands.value) return;
      console.log("editField called with field:", field); // Log field
      if (field === "description") {
        console.log("Setting isDescriptionOverlayVisible to true");
        isDescriptionOverlayVisible.value = true;
      } else if (field === "category") {
        console.log("Setting isCategoryOverlayVisible to true");
        isCategoryOverlayVisible.value = true;
      } else if (field === "logo") {
        console.log("Setting isLogoOverlayVisible to true");
        isLogoOverlayVisible.value = true;
      }
    };

    const updateDescription = async () => {
      console.log("updateDescription called");
      if (isTextTooLong.value) {
        toast.warn(
          `Description cannot exceed ${descriptionLimit.value} characters`,
          {
            autoClose: 3000,
          }
        );
        return;
      }
      if (!canUsePrivateOrganisationCommands.value) return null;
      isSaving.value = true;
      try {
        const description = newDescription.value.trim() || null;
        await updatePrivateOrganisationSettings({ description });
        isDescriptionOverlayVisible.value = false;
        toast.success("Description updated successfully.");
      } catch (error) {
        console.error("Failed to update description:", error);
        toast.error(error?.message || "Failed to update description.");
      } finally {
        isSaving.value = false;
      }
      return null;
    };

    const updateCategory = async () => {
      if (!canUsePrivateOrganisationCommands.value) return null;
      isSaving.value = true;
      try {
        const category = newCategory.value.trim() || null;
        await updatePrivateOrganisationSettings({ category });
        isCategoryOverlayVisible.value = false;
        toast.success("Category updated successfully!");
      } catch (error) {
        console.error("Failed to update category:", error);
        toast.error(error?.message || "Failed to update category.");
      } finally {
        isSaving.value = false;
      }
      return null;
    };

    const updateAllowTrades = async (newValue) => {
      if (!canUsePrivateOrganisationCommands.value) return null;
      isSaving.value = true;
      try {
        // An organisation that trades with others must be visible to them.
        const current = privateSettings.value || readPrivateSettings();
        const becomesPublic =
          newValue === true && current.visibility === "private";
        await updatePrivateOrganisationSettings({
          economy: { open: newValue === true },
          ...(becomesPublic ? { visibility: "public" } : {}),
        });
        toast.success(
          becomesPublic
            ? "Economy opened. The organisation is now public so its members can be paid."
            : "Economy mode updated successfully!"
        );
      } catch (error) {
        console.error("Error updating economy mode:", error);
        toast.error(error?.message || "Failed to update Economy mode.");
      } finally {
        isSaving.value = false;
      }
      return null;
    };

    return {
      isOrgAppearanceFeatureEnabled,
      isPrivateOrganisationContextMode,
      canManageAppearance,
      canUsePrivateOrganisationCommands,
      canUsePrivateLogoCommands,
      openAppearance,
      tableRows,
      showEditButton,

      toggleSwitchState,
      resolvedUserRolePrivileges,

      isDescriptionOverlayVisible,
      newDescription,
      isCategoryOverlayVisible,
      newCategory,
      isTextTooLong,
      descriptionLimit,
      characterCount,
      editField,
      updateDescription,
      updateCategory,
      updateAllowTrades,
      isDescriptionExpanded,
      shouldShowReadMoreButton,
      toggleDescription,

      existingLogoUrls,
      isLogoOverlayVisible,
      isLogoRemoved,
      newLogoUrl,
      isSaving,

      handleLogoUpdates,
      removeLogo,
      saveLogo,
    };
  },
};
</script>

<style scoped>
.organisation-design {
  display: grid;
  gap: var(--space-4);
}

.organisation-design__heading,
.organisation-design__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
}

.organisation-design__actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.organisation-design__heading h2,
.organisation-design__heading p,
.organisation-design__supporting {
  margin: 0;
}

.organisation-design__eyebrow {
  color: var(--color-accent-emphasis);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.organisation-design__supporting {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-label);
}

.strikethrough {
  margin-left: 5px;
  text-decoration: line-through;
}
.month-abbreviation {
  margin-left: 5px;
}

textarea {
  min-height: calc(var(--control-height-default) * 4);
  height: calc(var(--control-height-default) * 4);
  resize: none;
}

.logo-img {
  height: var(--control-height-default);
}

.no-border-bottom {
  border-bottom: none !important;
}
</style>
